/** Suggest how many vehicles are needed when a job exceeds max single-bus capacity. */
export function suggestSplitVehicleCount(
  passengerCount: number,
  maxCapacity: number
): number {
  if (maxCapacity < 1 || passengerCount < 1) return 2;
  if (passengerCount <= maxCapacity) return 1;
  return Math.max(2, Math.ceil(passengerCount / maxCapacity));
}

export type SplitCountsValidation =
  | { ok: true }
  | {
      ok: false;
      code:
        | 'need_at_least_two'
        | 'invalid_count'
        | 'exceeds_max'
        | 'sum_mismatch';
    };

/** Validate operator-entered per-vehicle passenger counts. */
export function validateSplitPassengerCounts(
  originalPassengers: number,
  maxCapacity: number,
  counts: number[]
): SplitCountsValidation {
  if (counts.length < 2) return { ok: false, code: 'need_at_least_two' };
  let sum = 0;
  for (const n of counts) {
    if (!Number.isInteger(n) || n < 1) return { ok: false, code: 'invalid_count' };
    if (n > maxCapacity) return { ok: false, code: 'exceeds_max' };
    sum += n;
  }
  if (sum !== originalPassengers) return { ok: false, code: 'sum_mismatch' };
  return { ok: true };
}

/** Even split seed for the wizard (operator may edit). */
export function seedSplitPassengerCounts(
  originalPassengers: number,
  maxCapacity: number,
  vehicleCount?: number
): number[] {
  const n = vehicleCount ?? suggestSplitVehicleCount(originalPassengers, maxCapacity);
  if (n < 2) return [originalPassengers];
  const base = Math.floor(originalPassengers / n);
  const rem = originalPassengers % n;
  const counts: number[] = [];
  for (let i = 0; i < n; i++) {
    const c = base + (i < rem ? 1 : 0);
    counts.push(Math.min(maxCapacity, Math.max(1, c)));
  }
  // Fix sum if capped by maxCapacity (rare edge: force redistribute upward vehicle count)
  let sum = counts.reduce((a, b) => a + b, 0);
  if (sum !== originalPassengers) {
    // Fall back to greedy fill
    const out = Array.from({ length: n }, () => 0);
    let left = originalPassengers;
    let i = 0;
    while (left > 0) {
      const room = maxCapacity - out[i % n];
      if (room > 0) {
        out[i % n] += 1;
        left -= 1;
      }
      i += 1;
      if (i > originalPassengers * n + 10) break;
    }
    return out;
  }
  return counts;
}
